"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TaskbarComponent = void 0;
const core_1 = require("@angular/core");
const common_1 = require("@angular/common");
const rxjs_1 = require("rxjs");
const focus_service_1 = require("../../services/focus.service");
let TaskbarComponent = class TaskbarComponent {
    constructor() {
        this.openWindows = core_1.input.required();
        this.focusWindow = (0, core_1.output)();
        this.minimizeWindow = (0, core_1.output)();
        this.toggleStartMenu = (0, core_1.output)();
        this.showDesktop = (0, core_1.output)();
        this.launchWithFile = (0, core_1.output)();
        this.currentTime = (0, core_1.signal)(new Date());
        this.batteryStatus = (0, core_1.signal)(null);
        this.dragOverAppId = (0, core_1.signal)(null);
        this.platformId = (0, core_1.inject)(core_1.PLATFORM_ID);
        this.destroyRef = (0, core_1.inject)(core_1.DestroyRef);
        this.focusService = (0, core_1.inject)(focus_service_1.FocusService);
        this.activeAppId = this.focusService.activeAppId;
        this.runningApps = (0, core_1.computed)(() => {
            const windows = this.openWindows();
            const appMap = new Map();
            for (const win of windows) {
                if (!appMap.has(win.appId) || win.zIndex > appMap.get(win.appId).zIndex) {
                    appMap.set(win.appId, win);
                }
            }
            return Array.from(appMap.values()).sort((a, b) => a.id - b.id);
        });
        (0, rxjs_1.interval)(1000).subscribe(() => {
            this.currentTime.set(new Date());
        });
        if ((0, common_1.isPlatformBrowser)(this.platformId)) {
            this.initBatteryMonitor();
        }
    }
    initBatteryMonitor() {
        if ('getBattery' in navigator) {
            navigator.getBattery().then((battery) => {
                const updateStatus = () => {
                    this.batteryStatus.set({
                        level: battery.level,
                        charging: battery.charging,
                    });
                };
                updateStatus();
                battery.addEventListener('levelchange', updateStatus);
                battery.addEventListener('chargingchange', updateStatus);
                this.destroyRef.onDestroy(() => {
                    battery.removeEventListener('levelchange', updateStatus);
                    battery.removeEventListener('chargingchange', updateStatus);
                });
            });
        }
    }
    onAppClick(window) {
        if (this.activeAppId() === window.appId) {
            this.minimizeWindow.emit(window.id);
        }
        else {
            this.focusWindow.emit(window.id);
        }
    }
    onDragOver(event, appId) {
        const isFileDrag = event.dataTransfer?.types.includes('application/webos-file-path');
        if (isFileDrag) {
            event.preventDefault();
            this.dragOverAppId.set(appId);
        }
    }
    onDragLeave() {
        this.dragOverAppId.set(null);
    }
    onDrop(event, appId) {
        event.preventDefault();
        this.dragOverAppId.set(null);
        const filePath = event.dataTransfer?.getData('application/webos-file-path');
        if (filePath) {
            this.launchWithFile.emit({ appId, filePath });
        }
    }
};
exports.TaskbarComponent = TaskbarComponent;
exports.TaskbarComponent = TaskbarComponent = __decorate([
    (0, core_1.Component)({
        selector: 'app-taskbar',
        standalone: true,
        // Fix: Set ChangeDetectionStrategy to OnPush for better performance with signal-based components.
        changeDetection: core_1.ChangeDetectionStrategy.OnPush,
        template: `
<div class="absolute bottom-0 left-0 right-0 h-12 flex justify-center z-[1000]">
  <div class="flex items-center justify-between px-4 bg-zinc-800/70 backdrop-blur-xl rounded-t-lg shadow-lg w-full max-w-7xl">
    <!-- Pinned Apps & Start Menu -->
    <div class="flex items-center gap-2">
      <!-- Windows Start Icon -->
      <button (click)="toggleStartMenu.emit()" class="p-2 rounded-md hover:bg-white/10">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
          <path d="M3 3a1 1 0 00-1 1v4.586A1 1 0 003.414 10H8V3H3zM12 3v7h4.586a1 1 0 00.707-1.707L12 3zM3 12v4.586A1 1 0 004.414 18H8v-6H3zm9 0v6h4.586a1 1 0 00.707-1.707L12 12z"/>
        </svg>
      </button>

      @for (app of runningApps(); track app.id) {
        <button 
            (click)="onAppClick(app)" 
            (dragover)="onDragOver($event, app.appId)"
            (dragleave)="onDragLeave()"
            (drop)="onDrop($event, app.appId)"
            class="relative p-2 rounded-md hover:bg-white/10 transition-colors duration-200"
            [class.bg-blue-500/30]="dragOverAppId() === app.appId">
          <img [src]="app.icon" [alt]="app.title" class="h-7 w-7 drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]">
          <span 
            class="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 bg-blue-400 rounded-full transition-all duration-200"
            [class.w-4]="activeAppId() !== app.appId"
            [class.w-6]="activeAppId() === app.appId">
          </span>
        </button>
      }
    </div>

    <!-- System Tray & Clock -->
    <div class="flex items-center gap-4">
        <!-- System Tray -->
        <div class="flex items-center gap-3 text-gray-200">
             <button title="Show hidden icons" class="p-1 rounded hover:bg-white/10">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                </svg>
             </button>
             <button title="Network" class="p-1 rounded hover:bg-white/10">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.136 11.886a9.75 9.75 0 0113.728 0" />
                </svg>
             </button>
             <button title="Volume" class="p-1 rounded hover:bg-white/10">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.114 8.634a.75.75 0 01.634 1.32l-4.5 2.25a.75.75 0 01-1.014-.543V7.027a.75.75 0 011.014-.543l4.5 2.25zM12 8.25a.75.75 0 01.75.75v6a.75.75 0 01-1.5 0v-6a.75.75 0 01.75-.75zM4.886 8.634a.75.75 0 011.014.543v6.646a.75.75 0 01-1.014.543l-4.5-2.25a.75.75 0 010-1.32l4.5-2.25a.75.75 0 01.634-.22z" />
                </svg>
             </button>
             @if (batteryStatus(); as status) {
                <button class="p-1 rounded hover:bg-white/10" [title]="'Battery: ' + (status.level * 100).toFixed(0) + '%'">
                    @if (status.charging) {
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                           <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 7.5V15a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V7.5" /><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 7.5V6a2.25 2.25 0 012.25-2.25h3a2.25 2.25 0 012.25 2.25v1.5M12 10.5v3.75m-2.25-1.5L12 10.5l2.25 1.5" />
                        </svg>
                    } @else if (status.level > 0.75) {
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M21 10.5h.375c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125H21M3.75 10.5H21m-17.25 0V7.5a2.25 2.25 0 012.25-2.25h12.75c1.243 0 2.25 1.007 2.25 2.25v3M3.75 10.5v3.75c0 1.243 1.007 2.25 2.25 2.25h12.75c1.243 0 2.25-1.007 2.25-2.25v-3.75M3.75 10.5h17.25" />
                        </svg>
                    } @else if (status.level > 0.25) {
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                           <path stroke-linecap="round" stroke-linejoin="round" d="M21 10.5h.375c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125H21M12 4.5v15m0 0l-3.75-3.75M12 19.5l3.75-3.75" />
                        </svg>
                    } @else {
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M21 10.5h.375c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125H21m-2.25-9v1.5m-3.75-1.5v1.5m-3.75-1.5v1.5m-3.75-1.5v1.5m-3.75 0v3.75c0 1.243 1.007 2.25 2.25 2.25h12.75c1.243 0 2.25-1.007 2.25-2.25v-3.75" />
                        </svg>
                    }
                </button>
             }
        </div>
        <!-- Clock -->
        <div class="text-xs text-right text-gray-200 pl-3 border-l border-white/20 cursor-pointer hover:bg-white/10 p-2 rounded">
          <div>{{ currentTime() | date:'h:mm a' }}</div>
          <div>{{ currentTime() | date:'M/d/yyyy' }}</div>
        </div>
    </div>
  </div>
  <!-- Show Desktop Button -->
  <button (click)="showDesktop.emit()" title="Show Desktop" class="absolute right-0 top-0 h-full w-1 border-l border-white/20 hover:bg-white/10 transition-colors"></button>
</div>`,
        imports: [common_1.CommonModule],
    }),
    __metadata("design:paramtypes", [])
], TaskbarComponent);
